import { type FolderTree } from "@mg/schemas/src/commons";
import {
  ArrowLineLeft,
  CaretRight,
  CaretDown,
  Minus,
  Trash,
} from "@phosphor-icons/react";
import * as Accordion from "@radix-ui/react-accordion";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import {
  Button,
  Dialog,
  Flex,
  IconButton,
  Link,
  ScrollArea,
  Text,
} from "@radix-ui/themes";
import { useQueryClient } from "@tanstack/react-query";
import {
  // eslint-disable-next-line import/named
  Link as NavLink,
  useNavigate,
  useRouter,
  useRouterState,
  useSearch,
} from "@tanstack/react-router";
import cx from "classnames";
import { usePostHog } from "posthog-js/react";
import { useMemo, useRef, useState } from "react";

import { CreateFolderDialog } from "./dialogs/CreateFolderDialog";
import {
  SIDEBAR_ACTIVE_ROW_CLASS,
  SIDEBAR_ITEM_INDENT,
  SIDEBAR_ITEM_TITLE_CLASS,
  SIDEBAR_ROW_FADE_STYLE,
  SIDEBAR_SECTION_HEADER_INDENT,
  SidebarActiveBar,
} from "./sidebar/sidebarItemStyles";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "./sidebar/SidebarTooltip";
import { useSidebar } from "./sidebar/useSidebar";

import { useUI } from "../contexts/ui";
import { ticketsRoute } from "../routes/tickets/route";
import { errorAnalyticsPayload } from "../utils/analytics";
import { batch } from "../utils/batch";
import { isNil } from "../utils/fp";
import { useAppSelector } from "../utils/hooks";
import { errorMessage } from "../utils/http";
import {
  useEnterprises,
  useSwitchEnterprise,
} from "../utils/queries/enterprises";
import { useFolderTree } from "../utils/queries/folders";
import {
  useDeleteFolderMutation,
  useEditProjectMutation,
  useUpdateFolderMutation,
} from "../utils/queries/projects";

type AppDrawerProps = {
  open: boolean;
  onClose(): void;
};

export function AppDrawer(props: AppDrawerProps) {
  const { open, onClose } = props;
  const posthog = usePostHog();
  const { data: enterprises } = useEnterprises();
  const switchEnterpriseMutation = useSwitchEnterprise();
  const enterpriseId = useAppSelector(
    (state) => state.auth.value?.enterpriseId,
  );
  const userEmail = useAppSelector((state) => state.auth.value?.email);

  const currentEnterprise = enterprises?.find((e) => e._id === enterpriseId);

  return (
    <aside
      className={cx(
        "fixed left-0 z-[4] h-screen w-80 bg-base-white shadow-lg transition-transform",
        {
          "-translate-x-80": !open,
        },
      )}
    >
      <header className="flex items-center justify-between border-b border-b-puntt-neutral-gray-6 p-4">
        <IconButton
          data-testid="app-drawer-icon-button-70"
          size="1"
          variant="soft"
          color="gray"
          onClick={onClose}
        >
          <ArrowLineLeft />
        </IconButton>

        {!enterprises?.length ? null : enterprises?.length === 1 ? (
          <Text size="2" weight="medium" className="ml-2">
            {currentEnterprise?.name}
          </Text>
        ) : (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <Button
                variant="soft"
                color="gray"
                className="ml-2"
                loading={switchEnterpriseMutation.isPending}
              >
                <Text
                  size="2"
                  weight="medium"
                  className="max-w-[180px] truncate"
                >
                  {currentEnterprise?.name || "Select Enterprise"}
                </Text>
                <CaretDown className="ml-1" />
              </Button>
            </DropdownMenu.Trigger>

            <DropdownMenu.Portal>
              <DropdownMenu.Content
                className="min-w-[220px] rounded-md bg-base-white p-1 shadow-lg"
                sideOffset={5}
              >
                {enterprises
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((enterprise) => (
                    <DropdownMenu.Item
                      key={enterprise._id}
                      data-id={enterprise._id}
                      className={cx(
                        "flex items-center rounded-sm px-2 py-1.5 outline-none",
                        "hover:bg-puntt-neutral-gray-4 focus:bg-puntt-neutral-gray-4",
                        {
                          "bg-puntt-neutral-gray-2":
                            enterprise._id === enterpriseId,
                          "cursor-pointer": enterprise._id !== enterpriseId,
                        },
                      )}
                      onClick={() => {
                        if (enterprise._id !== enterpriseId && enterprise._id) {
                          const start = performance.now();

                          switchEnterpriseMutation.mutate(enterprise._id, {
                            onSuccess: () => {
                              posthog.capture("switch_enterprise", {
                                from: enterpriseId,
                                to: enterprise._id,
                                duration_ms: performance.now() - start,
                                user: userEmail,
                              });
                              location.reload();
                            },
                          });
                        }
                      }}
                    >
                      <Text size="2">{enterprise.name}</Text>
                    </DropdownMenu.Item>
                  ))}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        )}
      </header>

      <FolderTreeView />
    </aside>
  );
}

export function FolderTreeView() {
  // Read the folder tree from React Query instead of the auth-layout
  // loader. The loader now kicks off a non-blocking prefetch (see
  // `routes/auth-layout/route.tsx`), so this hook hydrates from the
  // primed cache on the happy path and falls back to a fresh fetch if
  // the prefetch is still in flight. Using `useFolderTree()` keeps the
  // tree reactive to background refetches and decouples the sidebar
  // from a single blocking loader call.
  const { data: folderTree = [] } = useFolderTree();
  const { matches } = useRouterState();
  const isOnTicketsPage = matches.find(
    (m) => m.pathname.includes("/tickets") || m.pathname.includes("/ticket"),
  );
  const { open } = useSidebar();

  const user = useAppSelector((state) => state.auth.value);

  const sortedFolderTree = useMemo(() => {
    return folderTree.sort((a: any, b: any) => a.name.localeCompare(b.name));
  }, [folderTree]);

  const [createFolderDialogOpen, setCreateFolderDialogOpen] = useState(false);

  if (!open || isNil(user?.email) || !isOnTicketsPage) return null;

  function renderTree() {
    // Empty folder tree intentionally renders nothing — the "Documents"
    // pane is a folder list, not a per-document list, so a generic
    // "Add Your First Document" empty-state was misleading. The Projects
    // and Chats sections in `AppSidebar` follow the same pattern (hide
    // the section entirely when there's nothing to show).
    if (!sortedFolderTree.length) {
      return null;
    }

    return (
      <div className="flex min-h-0 shrink flex-col px-2">
        <div
          className={`relative flex w-full shrink-0 items-center gap-4 rounded-md ${SIDEBAR_SECTION_HEADER_INDENT} py-1.5 pr-2`}
          style={{ background: "inherit" }}
          data-testid="folder-tree-header"
        >
          <Text size="2" weight="bold" className="flex-1 truncate px-0">
            Folders
          </Text>
          <div className="absolute right-3 top-1/2 flex h-full -translate-y-1/2 items-center">
            <Button
              data-testid="app-drawer-button-195"
              size="1"
              variant="ghost"
              color="gray"
              className="hover:bg-puntt-neutral-gray-4"
              style={{ zIndex: 2 }}
              onClick={() => {
                setCreateFolderDialogOpen(true);
              }}
              tabIndex={0}
              aria-label="Add Folder"
            >
              +
            </Button>
          </div>
        </div>
        <ScrollArea
          type="auto"
          scrollbars="vertical"
          className="min-h-0 overflow-x-hidden"
          data-testid="folder-tree-list"
        >
          {sortedFolderTree.map((folder: any) => (
            <Folder key={folder._id} {...folder} />
          ))}
        </ScrollArea>
      </div>
    );
  }

  return (
    <>
      <CreateFolderDialog
        open={createFolderDialogOpen}
        onOpenChange={setCreateFolderDialogOpen}
        folderId={undefined}
      />
      {renderTree()}
    </>
  );
}

function Folder(props: FolderTree) {
  const { _id, name, folders = [] } = props;
  const [isOver, setIsOver] = useState(false);
  const posthog = usePostHog();
  const { notify } = useUI();
  const updateFolderMutation = useUpdateFolderMutation();
  const editProjectMutation = useEditProjectMutation();
  const deleteFolderMutation = useDeleteFolderMutation();
  const queryClient = useQueryClient();
  const router = useRouter();
  const navigate = useNavigate({ from: ticketsRoute.to });
  const { folderId } = useSearch({ strict: false });
  const elem = useRef<HTMLDivElement>(null);
  const nameRef = useRef<HTMLSpanElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const isActive = folderId === _id;

  function handleDeleteFolder() {
    deleteFolderMutation.mutate(_id as string, {
      onSuccess() {
        setConfirmDelete(false);
        // The sidebar reads the tree from the React Query `["folders","tree"]`
        // cache (NOT a route loader), so invalidate that key to drop the
        // deleted row. Also refresh the Documents grid query + the route.
        queryClient.invalidateQueries({ queryKey: ["folders", "tree"] });
        queryClient.invalidateQueries({ queryKey: ["folders-and-tickets"] });
        // If you were viewing the folder you just deleted, clear the
        // `folderId` search param so the grid doesn't sit on a dead folder
        // (mirrors the tickets-page + chat/project delete navigate-away).
        if (folderId === _id) {
          navigate({ search: ({ folderId: _omit, ...rest }) => rest });
        }
        router.invalidate();
        posthog.capture("folder_deleted", { folder_id: _id });
      },
      onError() {
        notify({
          title: "Unable to delete folder",
          message: "Please try again.",
          variant: "error",
        });
      },
    });
  }

  const onMoveToFolder = async (
    draggedIds: { _id: string; isFolder: boolean }[],
    targetFolderId: string,
  ) => {
    const startTime = performance.now();

    try {
      await batch(
        draggedIds,
        1,
        async ([dragTargetId]) => {
          if (dragTargetId.isFolder) {
            await updateFolderMutation.mutateAsync({
              _id: dragTargetId._id,
              parentFolder: targetFolderId,
            });
          } else {
            await editProjectMutation.mutateAsync({
              _id: dragTargetId._id,
              folder: targetFolderId,
            });
          }
        },
        5,
      );
      router.invalidate();
      notify({
        title: "Moved to folder",
        message: `${draggedIds.length} item${draggedIds.length === 1 ? "" : "s"} moved to ${name}`,
        variant: "success",
      });
      posthog.capture("tickets_dragged_to_sidebar", {
        folder_id: targetFolderId,
        dropped_ids: draggedIds,
        duration_ms: performance.now() - startTime,
        num_dropped: draggedIds.length,
      });
    } catch (error) {
      const message = await errorMessage(error);
      notify({
        title: "Error dragging to folder",
        message,
        variant: "error",
      });
      posthog.capture("tickets_dragged_to_sidebar_error", {
        folder_id: targetFolderId,
        num_dropped: draggedIds.length,
        dropped_ids: draggedIds,
        duration_ms: performance.now() - startTime,
        ...errorAnalyticsPayload(error),
      });
    }

    router.invalidate();
  };

  function getDragData<T>(e: React.DragEvent<T>) {
    try {
      const parsed = JSON.parse(
        e.dataTransfer?.getData("application/json") ?? "",
      ) as {
        _id: string;
        isFolder: boolean;
      }[];
      if (isNil(parsed)) return;

      // Prevent making a folder its own parent
      const isDropOnSelf = parsed.some(
        (item: { _id: string }) => item._id === _id,
      );
      if (isDropOnSelf) {
        console.warn("Cannot drop a folder onto itself");
        return;
      }

      // Prevent circular folder nesting
      const hasInvalidNesting = parsed.some((item: any) => {
        if (!item.isFolder) return false;

        // Find if the drop target is inside the dragged folder
        const draggedFolderSelector = `[data-folder-id="${item._id}"]`;
        return !isNil(elem.current?.closest(draggedFolderSelector));
      });

      if (hasInvalidNesting) {
        console.warn("Cannot move a folder into one of its sub-folders");
        return;
      }

      return parsed;
    } catch (err) {
      console.error("Invalid drag data", err);
      return null;
    }
  }

  function handleMouseEnter() {
    if (nameRef.current) {
      setShowTooltip(nameRef.current.scrollWidth > nameRef.current.clientWidth);
    }
  }

  function handleMouseLeave() {
    setShowTooltip(false);
  }

  return (
    <>
      <Accordion.Root
        ref={elem}
        type="single"
        collapsible
        data-folder-id={_id}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          e.dataTransfer.dropEffect = "move";
          setIsOver(true);
        }}
        onDragLeave={() => {
          setIsOver(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsOver(false);

          const parsed = getDragData(e);
          if (isNil(parsed)) return;

          onMoveToFolder(parsed, _id!);
        }}
      >
        <Accordion.Item value={_id as string}>
          <Accordion.Header
            data-active={isActive ? "true" : "false"}
            className={cx(
              // No flex `gap-2` on the row: the inter-item gutter lives on the
              // chevron (`mr-2`, applied only when it is shown). A row-level
              // `gap-2` reserved an ~8px gutter AFTER the collapsed zero-width
              // chevron on LEAF folders, pushing their title that much further
              // right than the chevron-less Ask Puntt project / chat rows (which
              // sit flush at SIDEBAR_ITEM_INDENT / pl-7). With the gutter on the
              // chevron instead, a leaf folder has none and its title aligns to
              // the same left edge as the Ask Puntt item rows.
              `group/folder-item relative flex w-full items-center rounded-md ${SIDEBAR_ITEM_INDENT} py-1.5 pr-2 hover:bg-puntt-neutral-gray-3`,
              // Drag-over drop target wins over the active wash.
              isActive && !isOver && SIDEBAR_ACTIVE_ROW_CLASS,
              {
                "rounded-lg bg-puntt-accent-10 text-base-white": isOver,
              },
            )}
          >
            {isActive && !isOver ? <SidebarActiveBar /> : null}
            <Accordion.Trigger
              asChild
              className={cx("group", {
                // Parent folder: the chevron IS the leading glyph (sits at pl-7,
                // aligned with the Ask Puntt item titles) and `mr-2` spaces the
                // title from it. Leaf folder: collapse the chevron to zero width
                // with NO trailing margin so the title is flush at pl-7.
                "mr-2": folders.length > 0,
                "w-0 overflow-hidden opacity-0": !folders.length,
              })}
            >
              <IconButton size="1" variant="ghost">
                <CaretRight
                  className="transition-transform group-data-[state=open]:rotate-90"
                  color="rgb(var(--puntt-neutral-gray-11))"
                />
              </IconButton>
            </Accordion.Trigger>
            <Tooltip open={showTooltip} delayDuration={200}>
              <TooltipTrigger asChild>
                <Link
                  asChild
                  className="w-full flex-1 truncate"
                  size="2"
                  draggable="true"
                  onDragStart={(e) => {
                    e.stopPropagation();
                    // Set data for folder being dragged
                    const dragData = JSON.stringify([{ _id, isFolder: true }]);
                    e.dataTransfer.setData("application/json", dragData);
                    e.dataTransfer.effectAllowed = "move";
                  }}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  <NavLink
                    to={ticketsRoute.to}
                    search={(prev) => ({ ...prev, folderId: _id })}
                    className="w-full overflow-hidden"
                  >
                    <span
                      ref={nameRef}
                      data-testid={`folder-row-title-${_id}`}
                      className={cx(
                        SIDEBAR_ITEM_TITLE_CLASS,
                        isOver && "!text-base-white",
                      )}
                      // Fade the right edge into the trailing delete button so it
                      // reads as an integrated part of the row (consistent with
                      // the Ask Puntt chat / project rows).
                      style={SIDEBAR_ROW_FADE_STYLE}
                    >
                      {name}
                    </span>
                  </NavLink>
                </Link>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={8} align="start">
                {name}
              </TooltipContent>
            </Tooltip>
            <div className="absolute right-3 top-1/2 flex h-full -translate-y-1/2 items-center">
              {/* Folder rows DELETE (you don't add to a folder from here). Solid,
                seamless part of the row by default (no bg, no shadow, no
                border); a RED highlight box appears behind the icon only on
                button-hover — matching the chat / project delete affordance. */}
              <IconButton
                data-testid={`folder-row-delete-${_id}`}
                size="2"
                variant="ghost"
                color="gray"
                className="hidden hover:bg-puntt-red-9 hover:text-base-white group-hover/folder-item:flex"
                style={{ zIndex: 2 }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setConfirmDelete(true);
                }}
                aria-label="Delete folder"
              >
                <Minus size={12} weight="bold" />
              </IconButton>
            </div>
          </Accordion.Header>

          <Accordion.Content className="ml-4">
            {folders.map((folder) => (
              <Folder key={folder._id} {...folder} />
            ))}
          </Accordion.Content>
        </Accordion.Item>
      </Accordion.Root>

      <Dialog.Root
        open={confirmDelete}
        onOpenChange={(open) => {
          if (!open) setConfirmDelete(false);
        }}
      >
        <Dialog.Content maxWidth="450px">
          <Dialog.Title>Delete folder?</Dialog.Title>
          <Dialog.Description>
            Are you sure you want to delete <em>{name}</em>? Its contents are
            removed from the folder.
          </Dialog.Description>
          <Flex gap="2" mt="3" justify="end">
            <Button
              variant="soft"
              color="gray"
              onClick={() => setConfirmDelete(false)}
              data-testid={`folder-row-delete-cancel-${_id}`}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleDeleteFolder}
              disabled={deleteFolderMutation.isPending}
              loading={deleteFolderMutation.isPending}
              data-testid={`folder-row-delete-confirm-${_id}`}
            >
              <Trash />
              Delete
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </>
  );
}
